/**
 * <MyEventsCard /> — Dashboard tile listing every event the signed-in user
 * has submitted, with quick archive / republish / edit actions.
 *
 * Backend: /events/mine/list · /events/{id}/archive · /events/{id}/republish
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Archive, ArchiveRestore, Pencil, ExternalLink, Loader2 } from "lucide-react";

const STATUS_COLOUR = {
  approved: "#16a34a",
  pending: "#f59e0b",
  archived: "#94a3b8",
  rejected: "#dc2626",
};

export default function MyEventsCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/events/mine/list");
      setItems(Array.isArray(data) ? data : []);
    } catch { /* silently — user might not have events */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (ev) => {
    if (!window.confirm(`Archive "${ev.title}"? It'll disappear from public listings until you republish.`)) return;
    setBusyId(ev.id);
    try {
      await http.post(`/events/${ev.id}/archive`);
      toast.success("Event archived.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't archive."); }
    finally { setBusyId(null); }
  };
  const republish = async (ev) => {
    setBusyId(ev.id);
    try {
      const { data } = await http.post(`/events/${ev.id}/republish`);
      toast.success(data.status === "approved" ? "Republished — live now!" : "Sent back for re-review.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't republish."); }
    finally { setBusyId(null); }
  };

  if (!loading && items.length === 0) return null;

  return (
    <div data-testid="my-events-card" className="wofy-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline" style={{ color: "var(--wofy-primary)" }}>
            <Calendar size={12} className="inline -mt-0.5 mr-1" /> YOUR EVENTS
          </div>
          <h2 className="font-display text-xl mt-1">Your event submissions</h2>
        </div>
        <Link to="/events" className="wofy-btn-ghost text-xs">Browse all <ExternalLink className="w-3 h-3" /></Link>
      </div>
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="space-y-2" data-testid="my-events-list">
          {items.slice(0, 8).map((ev) => {
            const clr = STATUS_COLOUR[ev.status] || "#64748b";
            const isBusy = busyId === ev.id;
            return (
              <div key={ev.id} className="flex items-center gap-3 rounded-lg border p-3" style={{ borderColor: "var(--wofy-line)" }} data-testid={`my-event-${ev.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{ev.title}</div>
                  <div className="text-xs mt-0.5 opacity-70">
                    {(ev.start_date || "").slice(0, 10)} · {ev.city || "—"}
                  </div>
                </div>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: `${clr}22`, color: clr }}>
                  {ev.status}
                  {ev.auto_archived && ev.status === "archived" && " · auto"}
                </span>
                <Link to={`/events/${ev.slug || ev.id}`} className="wofy-btn-ghost text-xs" data-testid={`my-event-view-${ev.id}`}>
                  <ExternalLink className="w-3 h-3" />
                </Link>
                {ev.status !== "archived" ? (
                  <button
                    onClick={() => archive(ev)}
                    disabled={isBusy}
                    className="wofy-btn-ghost text-xs"
                    title="Archive so you can edit and republish"
                    data-testid={`my-event-archive-${ev.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3" />}
                  </button>
                ) : (
                  <button
                    onClick={() => republish(ev)}
                    disabled={isBusy}
                    className="wofy-btn-ghost text-xs"
                    style={{ color: "var(--wofy-primary)" }}
                    title="Republish"
                    data-testid={`my-event-republish-${ev.id}`}
                  >
                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArchiveRestore className="w-3 h-3" />}
                    Republish
                  </button>
                )}
              </div>
            );
          })}
          {items.length > 8 && (
            <p className="text-xs text-center pt-2" style={{ color: "var(--wofy-text-soft)" }}>
              + {items.length - 8} more · showing latest 8
            </p>
          )}
        </div>
      )}
    </div>
  );
}
